"use client";

import React, { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="224311" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-16 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative group py-2 text-sm font-semibold tracking-tight text-foreground/90 hover:text-foreground transition-colors font-default">
                {item.label}
                <span className="absolute left-1/2 bottom-0 w-0 h-[2px] bg-primary group-hover:w-full group-hover:left-0 transition-all duration-300 ease-out" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-12">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-32 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <div className="flex flex-col gap-6 mt-12">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-semibold text-foreground/90 hover:text-primary transition-colors font-default" data-index={i}>
          {item.label}
        </Link>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 bg-background text-foreground border-b border-border/10 ${isScrolled ? "bg-background/80 backdrop-blur-md shadow-md" : ""}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <Link to="/" className="relative flex items-center group">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645726/img/image_p0dadlls.png" alt="RM Roofing & Waterproofing" className="h-30 relative z-10" />
        </Link>

        {}
        <div className="ml-auto">
          <MenuProvider menu_id="45">
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4">
          {}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden relative z-50 text-foreground hover:bg-accent/10">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background text-foreground border-l-border/20 w-[300px] sm:w-[400px]">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <MenuProvider menu_id="45">
                <MobileMenu />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}